import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';

interface CompanyRoleRouteProps {
  children: React.ReactNode;
  allowedRoles: readonly string[];
  redirectTo?: string;
}

/**
 * Protected route component that checks if user has required role in current company
 * Redirects to company selector if user has multiple companies or doesn't have required role
 * Similar to mobile app's role-based access checks
 */
export function CompanyRoleRoute({ 
  children, 
  allowedRoles, 
  redirectTo = '/company-selector' 
}: CompanyRoleRouteProps) {
  const navigate = useNavigate();
  const { data: companyId, role, allCompanies, isLoading } = useCurrentCompany();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Memoize allowedRoles as a sorted string for stable comparison
  // This prevents infinite re-renders when allowedRoles is passed as an inline array literal
  const allowedRolesKey = useMemo(() => {
    return [...allowedRoles].sort().join(',');
  }, [allowedRoles]);

  useEffect(() => {
    // Reset state when dependencies change to ensure re-validation
    setIsAuthorized(false);
    setIsRedirecting(false);

    // Don't start checking if data is still loading - wait for data to be ready
    if (isLoading) {
      setIsChecking(false); // Not checking yet, just waiting for data
      return;
    }

    // Now we can perform authorization check
    setIsChecking(true);

    // If no company selected, redirect to company selector
    if (!companyId || !role) {
      // If user has multiple companies, show selector
      if (allCompanies.length > 1) {
        setIsRedirecting(true);
        setIsChecking(false); // Done checking, redirecting
        navigate(redirectTo);
        return;
      }
      // If no companies, redirect to dashboard
      setIsRedirecting(true);
      setIsChecking(false); // Done checking, redirecting
      navigate('/dashboard');
      return;
    }

    // Check if user has required role
    // Use allowedRoles directly since allowedRolesKey is only for dependency tracking
    // The memoized key ensures the effect runs when roles change, even if array reference changes
    const hasRequiredRole = allowedRoles.includes(role);
    
    if (!hasRequiredRole) {
      // If user has multiple companies, redirect to selector so they can choose
      if (allCompanies.length > 1) {
        setIsRedirecting(true);
        setIsChecking(false); // Done checking, redirecting
        navigate(redirectTo);
        return;
      }
      // Single company without required role: redirect based on role
      // Students go to student-landing, others go to dashboard with error
      setIsRedirecting(true);
      setIsChecking(false); // Done checking, redirecting
      if (role === 'student') {
        navigate('/student-landing');
      } else {
        navigate('/dashboard?error=access_denied');
      }
      return;
    }

    // Authorization successful
    setIsAuthorized(true);
    setIsChecking(false);
    // Only depend on allowedRolesKey (memoized string) to prevent unnecessary re-renders
    // When allowedRoles is passed as inline array literal, allowedRolesKey changes only when roles actually change
    // Including allowedRoles in deps would cause effect to run on every render, negating memoization benefit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, role, allCompanies.length, allowedRolesKey, isLoading, navigate, redirectTo]);

  // Show loading state while redirecting to prevent blank screen
  if (isLoading || isChecking || isRedirecting) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // Will redirect (isRedirecting already handled above)
  }

  return children;
}

